import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  linkedSignal,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

import { DayOfWeek } from '@blaze-shared/api-contracts-domain';
import { isDeepEqual } from '@blaze-shared/shared-util';

import {
  AvailabilityScheduleConfig,
  AvailabilityScheduleErrors,
  AvailabilityScheduleRule,
  AvailabilityScheduleRuleTimeSlot,
} from '../../availability.model';
import { AvailabilityScheduleHeaderComponent } from '../availability-schedule-header/availability-schedule-header.component';
import { AvailabilityScheduleRuleComponent } from '../availability-schedule-rule/availability-schedule-rule.component';

@Component({
  selector: 'blaze-availability-schedule',
  templateUrl: './availability-schedule.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatSlideToggleModule,
    AvailabilityScheduleHeaderComponent,
    AvailabilityScheduleRuleComponent,
  ],
  host: { class: 'flex flex-col gap-8' },
})
export class AvailabilityScheduleComponent implements OnInit {
  model = input.required<AvailabilityScheduleConfig>();
  isFirst = input.required<boolean>();
  defaultTimeSlot = input.required<AvailabilityScheduleRuleTimeSlot>();
  visibleDays = input.required<DayOfWeek[]>();
  errors = input.required<AvailabilityScheduleErrors | undefined>();
  hasMissingConfig = input.required<boolean>();

  copySchedule = output();
  deleteSchedule = output();
  validateSchedule = output<AvailabilityScheduleConfig>();
  saveChanges = output<AvailabilityScheduleConfig>();

  private _isInitialized = signal(false);
  protected isExpanded = signal(true);
  protected hasErrors = computed(() => {
    const errors = this.errors();
    return errors && (!!Object.keys(errors.ruleErrors).length || !!errors.slotsErrors.length);
  });
  protected config = linkedSignal<AvailabilityScheduleConfig, AvailabilityScheduleConfig>({
    source: () => ({ ...this.model() }),
    computation: (value, prev) => {
      // when previously set model == updated model & updated != local state, return local state
      if (isDeepEqual(prev?.source, value) && !isDeepEqual(value, prev?.value)) {
        return prev?.value ?? value;
      }
      return value;
    },
  });
  canSave = computed(() => {
    return !isDeepEqual(this.model(), this.config());
  });

  isInitialized = computed(() => this._isInitialized());

  ngOnInit(): void {
    const status = this.model().status;
    if (!this.isFirst() && status === 'expired') {
      this.isExpanded.set(false);
    }
    this._isInitialized.set(true);
  }

  protected onConfigUpdate(updated: AvailabilityScheduleConfig): void {
    this.config.set(updated);
    this.validateSchedule.emit(updated);
  }

  protected onCopySlots(sourceRule: AvailabilityScheduleRule, targetDay: DayOfWeek): void {
    const config = this.config();
    this.config.set({
      ...config,
      rules: config.rules.map((rule) => {
        if (rule.day !== targetDay) {
          return rule;
        }
        const timeSlots = [...sourceRule.timeSlots.map((ts) => ({ ...ts, dataRowId: null }))];
        return { ...rule, enabled: true, timeSlots };
      }),
    });
  }

  protected onSaveChanges(): void {
    this.saveChanges.emit({ ...this.config() });
  }
}
